import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Calendar, Clock, Eye } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Badge } from "@/components/ui/badge"
import { fetchWithAuth } from "@/lib/api/client"

interface DeadlineItem {
  licitacion_id: string
  titulo: string
  fecha_presentacion: string
  dias_restantes: number
  estado: string
}

export function UpcomingDeadlines() {
  const [deadlines, setDeadlines] = useState<DeadlineItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchDeadlines() {
      try {
        const data = await fetchWithAuth("/api/v1/analytics/deadlines?days=30")
        setDeadlines(data)
      } catch (error) {
        console.error("Error fetching deadlines:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchDeadlines()
  }, [])

  const getUrgencyColor = (dias: number): string => {
    if (dias < 7) return "bg-red-100 text-red-800 border-red-200"
    if (dias <= 14) return "bg-yellow-100 text-yellow-800 border-yellow-200"
    return "bg-green-100 text-green-800 border-green-200"
  }

  const getUrgencyDotColor = (dias: number): string => {
    if (dias < 7) return "bg-red-500"
    if (dias <= 14) return "bg-yellow-500"
    return "bg-green-500"
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  const getEstadoBadgeColor = (estado: string): string => {
    // Alineado con backend/app/models/licitacion.py::EstadoLicitacion
    switch (estado) {
      case "BORRADOR":
        return "bg-gray-100 text-gray-800"
      case "EN_ANALISIS":
      case "EN_PREPARACION":
        return "bg-blue-100 text-blue-800"
      case "PRESENTADA":
      case "EN_EVALUACION":
        return "bg-purple-100 text-purple-800"
      case "ADJUDICADA":
        return "bg-green-100 text-green-800"
      case "RECHAZADA":
      case "DESIERTA":
      case "CANCELADA":
        return "bg-red-100 text-red-800"
      default:
        return "bg-slate-100 text-slate-800"
    }
  }

  if (loading) {
    return <UpcomingDeadlinesLoading />
  }

  if (deadlines.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Próximos Vencimientos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              No hay vencimientos próximos en los próximos 30 días
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Próximos Vencimientos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {deadlines.map((deadline) => (
            <div
              key={deadline.licitacion_id}
              className="flex items-center justify-between gap-4 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
            >
              {/* Left: Title and date */}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/licitaciones/${deadline.licitacion_id}`}
                  className="font-medium text-sm hover:underline line-clamp-1"
                >
                  {deadline.titulo}
                </Link>
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(deadline.fecha_presentacion)}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-xs ${getEstadoBadgeColor(deadline.estado)}`}
                  >
                    {deadline.estado}
                  </Badge>
                </div>
              </div>

              {/* Center: Days remaining badge */}
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={`px-3 py-1 text-xs font-semibold ${getUrgencyColor(deadline.dias_restantes)}`}
                >
                  <span
                    className={`inline-block h-1.5 w-1.5 rounded-full mr-2 ${getUrgencyDotColor(deadline.dias_restantes)}`}
                  />
                  {deadline.dias_restantes === 0
                    ? "Hoy"
                    : deadline.dias_restantes === 1
                      ? "1 día"
                      : `${deadline.dias_restantes} días`}
                </Badge>
              </div>

              {/* Right: Action button */}
              <Link to={`/licitaciones/${deadline.licitacion_id}`}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Eye className="h-4 w-4" />
                  <span className="sr-only">Ver licitación</span>
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function UpcomingDeadlinesLoading() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Próximos Vencimientos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-4 rounded-lg border p-3"
            >
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-8 w-8" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
